import { useEffect } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Callout } from '@/components/ui/callout';
import { Label } from '@/components/ui/label';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { useTranslation } from '@/hooks/useTranslation';
import { useBaseToolDraft } from '../hooks/useBaseToolDraft';
import type { BaseToolWire } from '../lib/baseToolSchema';
import { DEFAULT_BASE_TOOL } from '../lib/defaultBaseTool';
import { BaseToolFields } from './BaseToolFields';

interface BaseToolSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The tool currently applied to the run, edited again when the sheet opens. */
  tool: BaseToolWire | null;
  onApply: (tool: BaseToolWire) => void;
}

/** Side panel where the base tool is entered, field by field or as JSON. */
export function BaseToolSheet({ open, onOpenChange, tool, onApply }: BaseToolSheetProps) {
  const { t } = useTranslation();
  // A new tool starts from the catalogue's most typical one, never from 41 empty fields.
  const startingTool = tool ?? DEFAULT_BASE_TOOL;
  const editor = useBaseToolDraft(startingTool);
  const { reset, validation } = editor;

  // Closing without applying discards the draft: every opening starts from the applied tool.
  useEffect(() => {
    if (open) reset(startingTool);
  }, [open, startingTool, reset]);

  const handleApply = () => {
    if (!editor.canApply) return;
    onApply(editor.wireTool);
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        className="flex w-full flex-col gap-0 sm:max-w-3xl"
        data-testid="base-tool-sheet"
      >
        <SheetHeader className="gap-3 border-b border-border pr-12">
          <div>
            <SheetTitle>{t('toolRecommender.baseTool.sheetTitle')}</SheetTitle>
            <SheetDescription>{t('toolRecommender.baseTool.sheetDescription')}</SheetDescription>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-xs" role="status">
            <Badge
              data-testid="base-tool-filled"
              tone={validation.isComplete ? 'primary' : 'neutral'}
              className="tabular-nums"
            >
              {t('toolRecommender.baseTool.filled', {
                filled: validation.valid,
                total: validation.total,
              })}
            </Badge>
            {validation.invalid > 0 && (
              <Badge data-testid="base-tool-errors" tone="error" className="tabular-nums">
                {t('toolRecommender.baseTool.errors', { n: validation.invalid })}
              </Badge>
            )}
            <span className="h-1 min-w-24 flex-1 overflow-hidden rounded-full bg-muted" aria-hidden>
              <span
                className="block h-full bg-primary transition-[width]"
                style={{ width: `${(validation.valid / validation.total) * 100}%` }}
              />
            </span>
          </div>
        </SheetHeader>

        <Tabs defaultValue="form" className="flex min-h-0 flex-1 flex-col gap-0">
          <TabsList className="mx-4 mt-3 w-fit">
            <TabsTrigger value="form">{t('toolRecommender.baseTool.tabForm')}</TabsTrigger>
            <TabsTrigger value="json">{t('toolRecommender.baseTool.tabJson')}</TabsTrigger>
          </TabsList>

          <TabsContent value="form" className="min-h-0 flex-1 overflow-y-auto p-4">
            <BaseToolFields
              draft={editor.draft}
              issues={validation.issues}
              onChange={editor.setField}
            />
          </TabsContent>

          <TabsContent
            value="json"
            className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto p-4"
          >
            <Label htmlFor="base-tool-json" className="text-xs">
              {t('toolRecommender.baseTool.jsonLabel')}
            </Label>
            <Textarea
              id="base-tool-json"
              className="min-h-80 flex-1 font-mono text-xs"
              spellCheck={false}
              value={editor.jsonText}
              onChange={(event) => editor.setJsonText(event.target.value)}
              placeholder={t('toolRecommender.form.baseToolPlaceholder')}
              aria-invalid={editor.jsonProblem !== null}
            />
            {editor.jsonProblem && (
              <Callout tone="error" size="inline">
                {editor.jsonProblem.reason === 'syntax'
                  ? t('toolRecommender.baseTool.json.syntax', {
                      message: editor.jsonProblem.message,
                    })
                  : t('toolRecommender.baseTool.json.notAnObject')}
              </Callout>
            )}
            {editor.unknownKeys.length > 0 && (
              <p className="text-xs text-muted-foreground" data-testid="base-tool-unknown-keys">
                {t('toolRecommender.baseTool.json.unknownKeys', {
                  keys: editor.unknownKeys.join(', '),
                })}
              </p>
            )}
          </TabsContent>
        </Tabs>

        <SheetFooter className="flex-row flex-wrap justify-between border-t border-border">
          <div className="flex gap-1">
            <Button type="button" variant="ghost" onClick={editor.clear}>
              {t('toolRecommender.baseTool.clear')}
            </Button>
            <Button type="button" variant="ghost" onClick={() => reset(DEFAULT_BASE_TOOL)}>
              {t('toolRecommender.baseTool.restoreDefaults')}
            </Button>
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="button" onClick={handleApply} disabled={!editor.canApply}>
              {t('toolRecommender.baseTool.apply')}
            </Button>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
